import React, { useState, useEffect } from 'react';
import { Container, Paper, Box, TextField, Button, Typography, Alert, CircularProgress, InputAdornment, IconButton, Divider } from '@mui/material';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { login, loginWithMicrosoft } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import {
  clearMicrosoftRedirectStarted,
  completeMicrosoftRedirectLogin,
  signInWithMicrosoft,
  wasMicrosoftRedirectStarted,
} from '../utils/msalClient';
// Import the SelectivateLogo component
import SelectivateLogo from '../components/SelectivateLogo';

const isSsoDebugEnabled = process.env.NODE_ENV === 'development';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [microsoftLoading, setMicrosoftLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated } = useAuth();
  const [finishingMicrosoftRedirect, setFinishingMicrosoftRedirect] = useState(false);

  useEffect(() => {
    const storedSsoError = sessionStorage.getItem('microsoftSsoError');
    const ssoError = searchParams.get('sso_error') || storedSsoError;
    if (ssoError) {
      setError(ssoError);
      sessionStorage.removeItem('microsoftSsoError');
    }
  }, [searchParams]);

  useEffect(() => {
    if (!wasMicrosoftRedirectStarted() || window.location.pathname === '/auth/callback') {
      return;
    }

    let isMounted = true;

    const completeUnexpectedMicrosoftReturn = async () => {
      setFinishingMicrosoftRedirect(true);
      setMicrosoftLoading(true);
      setError('');

      try {
        if (isSsoDebugEnabled) {
          console.warn('[MSAL] Completing Microsoft redirect from login page', {
            currentUrl: window.location.href,
          });
        }
        const idToken = await completeMicrosoftRedirectLogin();

        if (!idToken) {
          throw new Error('Microsoft did not return an ID token.');
        }

        const response = await loginWithMicrosoft(idToken);

        if (!response.success) {
          throw new Error(response.message || 'Microsoft login failed.');
        }

        clearMicrosoftRedirectStarted();
        localStorage.setItem('currentUser', JSON.stringify(response.user));
        window.location.replace('/home');
      } catch (err) {
        clearMicrosoftRedirectStarted();
        if (isMounted) {
          setError(err.message || 'Microsoft login failed. Please try again.');
          setMicrosoftLoading(false);
          setFinishingMicrosoftRedirect(false);
        }
        console.error('Microsoft redirect completion error:', err);
      }
    };

    completeUnexpectedMicrosoftReturn();

    return () => {
      isMounted = false;
    };
  }, []);
  
  useEffect(() => {
    // If user is already authenticated, redirect to home
    if (isAuthenticated) {
      navigate('/home');
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await login({ email, password });
      if (response.success) {
        // Store user in localStorage and set in context
        localStorage.setItem('currentUser', JSON.stringify(response.user));
        window.location.href = '/home'; // Force a full page reload to update auth state
      } else {
        setError(response.message || 'Login failed. Please check your credentials.');
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
      console.error('Login error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleMicrosoftLogin = async () => {
    if (isSsoDebugEnabled) {
      console.warn('[MSAL] Microsoft sign-in button clicked', {
        currentUrl: window.location.href,
      });
    }
    setError('');

    setMicrosoftLoading(true);
    try {
      clearMicrosoftRedirectStarted();
      await signInWithMicrosoft();
    } catch (err) {
      setError(err.message || 'Microsoft login failed. Please try again.');
      console.error('Microsoft login error:', err);
      setMicrosoftLoading(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 'calc(100vh - 80px)',
        }}
      >
        <Box
          sx={{ 
            mb: 4, 
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center', 
          }}
        >
          <Box 
            component="img" 
            src="/SELECCIÓN_logo_PNG.png"
            alt="SELECCIÓN Consulting"
            sx={{ 
              width: 200, 
              mb: 2
            }}
          />
          {/* Replace SAP Functional Spec Generator with Selectivate logo */}
          <Box sx={{ mb: 2 }}>
            <SelectivateLogo variant="banner" size="large" />
          </Box>
          <Typography variant="subtitle1" color="textSecondary" sx={{ mb: 2 }}>
            Sign in to access your account
          </Typography>
        </Box>
        
        <Paper 
          elevation={4} 
          sx={{ 
            p: 4, 
            maxWidth: 450, 
            width: '100%',
            borderRadius: 2,
            background: 'linear-gradient(to bottom, #1E1E1E, #252525)',
            border: '1px solid rgba(255, 255, 255, 0.05)',
          }}
        >
          {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
          
          <form onSubmit={handleSubmit}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 1, color: '#E0E0E0', fontWeight: 500 }}>
                Email Address *
              </Typography>
              <TextField
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                fullWidth
                required
                autoFocus
                variant="outlined"
                placeholder="Enter your email address"
                InputProps={{
                  sx: { borderRadius: 1 }
                }}
              />
            </Box>
            
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" sx={{ mb: 1, color: '#E0E0E0', fontWeight: 500 }}>
                Password *
              </Typography>
              <TextField
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                fullWidth
                required
                variant="outlined"
                placeholder="Enter your password"
                InputProps={{
                  sx: { borderRadius: 1 },
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        sx={{ color: '#E0E0E0' }}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            
            <Box sx={{ textAlign: 'right', mb: 1 }}>
              <Link 
                to="/forgot-password" 
                style={{ 
                  textDecoration: 'none', 
                  color: '#4CAF50',
                  fontSize: '0.875rem',
                  fontWeight: 500
                }}
              >
                Forgot Password?
              </Link>
            </Box>
            
            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              size="large"
              sx={{ 
                mt: 1, 
                mb: 3, 
                py: 1.2,
                fontWeight: 600,
                borderRadius: 1,
                boxShadow: '0 4px 10px rgba(245, 130, 31, 0.3)',
                '&:hover': {
                  backgroundColor: '#f06000',
                  boxShadow: '0 6px 15px rgba(245, 130, 31, 0.4)',
                }
              }}
              disabled={loading || microsoftLoading}
            >
              {loading ? <CircularProgress size={24} /> : 'Sign In'}
            </Button>
          </form>

          <Divider sx={{ my: 2, color: 'text.secondary' }}>or</Divider>

          <Button
            type="button"
            variant="outlined"
            fullWidth
            size="large"
            onClick={handleMicrosoftLogin}
            disabled={loading || microsoftLoading}
            sx={{
              py: 1.2,
              borderRadius: 1,
              fontWeight: 600,
              borderColor: 'rgba(255, 255, 255, 0.25)',
              color: '#E0E0E0',
              '&:hover': {
                borderColor: '#F5821F',
                backgroundColor: 'rgba(245, 130, 31, 0.08)',
              },
            }}
          >
            {finishingMicrosoftRedirect ? (
              'Completing Microsoft sign in...'
            ) : microsoftLoading ? (
              <CircularProgress size={24} />
            ) : (
              'Sign in with Microsoft'
            )}
          </Button>
          
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Typography variant="body2">
              Don't have an account?{' '}
              <Link 
                to="/register" 
                style={{ 
                  textDecoration: 'none', 
                  color: '#F5821F',
                  fontWeight: 500
                }}
              >
                Register Now
              </Link>
            </Typography>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default LoginPage;
